import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';

export interface PaymentQuote {
  carbonVatRate?: number | null;
  carbonVatValue?: number | null;
  contributionType?: string | null;
  contributionValue?: number | null;
  contributionValueHarmonised?: number | null;
  costCarbonSubTotal?: number | null;
  costGrandTotal?: number | null;
  countryId?: string | null;
  countryLanguageCode?: string | null;
  currency?: string | null;
  dstTaxRate?: number | null;
  dstTaxValue?: number | null;
  tonnes?: number | null;
  transactionFeeIncVat?: number | null;
  transactionFeeRate?: number | null;
  transactionFeeValue?: number | null;
  txnVatRate?: number | null;
  txnVatValue?: number | null;
}

export interface PaymentState {
  error: NormalisedError | null;
  clientIntentSecret: string | null;
  fetchingPaymentIntent: boolean;
  quote: PaymentQuote | null;
  quoteApplicationId: number | null;
}

interface GetFormStateRejectedAction extends Action {
  type: 'GET_FORM_STATE_REJECTED';
}

interface GetFormStateFulfilledAction extends Action {
  type: 'GET_FORM_STATE_FULFILLED';
  id: number;
  quote: PaymentQuote | null;
}

interface ExecutePaymentStartedAction extends Action {
  type: 'EXECUTE_PAYMENT_STARTED';
}

interface ExecutePaymentFulfilledAction extends Action {
  type: 'EXECUTE_PAYMENT_FULFILLED';
  clientIntentSecret?: string;
}

interface ExecutePaymentRejectedAction extends Action {
  type: 'EXECUTE_PAYMENT_REJECTED';
  error: NormaliseErrorInput;
}

interface ClearPaymentErrorAction extends Action {
  type: 'CLEAR_PAYMENT_ERROR';
}

type PaymentAction =
  | GetFormStateRejectedAction
  | GetFormStateFulfilledAction
  | ExecutePaymentStartedAction
  | ExecutePaymentFulfilledAction
  | ExecutePaymentRejectedAction
  | ClearPaymentErrorAction;

const defaultState: PaymentState = {
  error: null,
  clientIntentSecret: null,
  fetchingPaymentIntent: false,
  quote: null,
  quoteApplicationId: null,
};

function reducer(state: PaymentState = defaultState, action: PaymentAction): PaymentState {
  switch (action.type) {
    case 'GET_FORM_STATE_REJECTED': {
      return {
        ...state,
        quote: null,
        quoteApplicationId: null,
      };
    }
    case 'GET_FORM_STATE_FULFILLED': {
      const { id: applicationId, quote } = action;
      return {
        ...state,
        quoteApplicationId: applicationId,
        quote,
      };
    }
    case 'EXECUTE_PAYMENT_STARTED': {
      return {
        ...state,
        clientIntentSecret: null,
        fetchingPaymentIntent: true,
      };
    }
    case 'EXECUTE_PAYMENT_FULFILLED': {
      if (!action?.clientIntentSecret) {
        return state;
      }
      return {
        ...state,
        clientIntentSecret: action.clientIntentSecret,
        fetchingPaymentIntent: false,
      };
    }
    case 'EXECUTE_PAYMENT_REJECTED': {
      return {
        ...state,
        clientIntentSecret: null,
        error: normaliseError(action.error),
      };
    }
    case 'CLEAR_PAYMENT_ERROR': {
      return {
        ...state,
        error: null,
      };
    }
    default:
      return state;
  }
}

export default reducer;
